(() => {
  if (customElements.get('size-recommender')) return;

  const calculationTypes = new Set([
    'exact_lookup',
    'same_size_ranges',
    'largest_size_ranges'
  ]);
  const inputTypes = new Set(['select', 'number']);
  let instanceCount = 0;

  class SizeRecommender extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = this.readConfig();

      if (!this.isValidConfig(this.config)) {
        this.remove();
        return;
      }

      this.scope = this.closest('[data-form-holder]') || document;
      this.nativeOptionName = `options[${this.config.targetOption}]`;

      if (!this.getNativeOptionInputs().length) {
        this.remove();
        return;
      }

      this.instanceId = `size-recommender-${++instanceCount}`;
      this.activeMethodIndex = 0;
      this.render();
      this.hidden = false;
    }

    readConfig() {
      const source = this.querySelector('[data-size-recommender-config]');

      try {
        return JSON.parse(source ? source.textContent : '');
      } catch (error) {
        return null;
      }
    }

    isValidConfig(config) {
      if (!config || typeof config !== 'object') return false;
      if (!config.title || !config.targetOption || !config.buttonLabel) return false;
      if (!config.messages || !config.messages.success || !config.messages.completeFields) return false;
      if (!Array.isArray(config.sizeOrder) || !config.sizeOrder.length) return false;
      if (!Array.isArray(config.methods) || !config.methods.length) return false;

      return config.methods.every((method) => {
        if (!method || !method.id || !method.label) return false;
        if (!Array.isArray(method.inputs) || !method.inputs.length) return false;
        if (!method.inputs.every((input) => {
          if (!input.id || !input.label || !inputTypes.has(input.type)) return false;
          if (input.type === 'select') return Array.isArray(input.options) && input.options.length > 0;
          return Number.isFinite(input.min) && Number.isFinite(input.max) && input.min > 0 && input.max >= input.min;
        })) return false;
        if (!method.calculation || !calculationTypes.has(method.calculation.type)) return false;

        if (method.calculation.type === 'exact_lookup') {
          return Array.isArray(method.calculation.inputOrder) &&
            method.calculation.inputOrder.length &&
            method.calculation.mapping &&
            typeof method.calculation.mapping === 'object';
        }

        return method.calculation.ranges && typeof method.calculation.ranges === 'object';
      });
    }

    render() {
      const card = document.createElement('div');
      card.className = 'size-recommender__card';

      const title = document.createElement('h3');
      title.className = 'size-recommender__title';
      title.id = `${this.instanceId}-title`;
      title.textContent = this.config.title;
      card.setAttribute('aria-labelledby', title.id);
      card.appendChild(title);

      if (this.config.methods.length > 1) {
        this.tabs = document.createElement('div');
        this.tabs.className = 'size-recommender__tabs';
        this.tabs.setAttribute('role', 'tablist');
        this.tabs.setAttribute('aria-label', this.config.title);

        this.config.methods.forEach((method, index) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'size-recommender__tab';
          tab.id = `${this.instanceId}-tab-${method.id}`;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', `${this.instanceId}-panel`);
          tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          tab.tabIndex = index === 0 ? 0 : -1;
          tab.textContent = method.label;
          tab.addEventListener('click', () => this.setMethod(index));
          this.tabs.appendChild(tab);
        });

        card.appendChild(this.tabs);
      }

      this.panel = document.createElement('div');
      this.panel.className = 'size-recommender__panel';
      this.panel.id = `${this.instanceId}-panel`;
      card.appendChild(this.panel);

      this.action = document.createElement('button');
      this.action.type = 'button';
      this.action.className = 'size-recommender__action';
      this.action.textContent = this.config.buttonLabel;
      this.action.addEventListener('click', () => this.calculate());
      card.appendChild(this.action);

      this.result = document.createElement('p');
      this.result.className = 'size-recommender__result';
      this.result.setAttribute('aria-live', 'polite');
      this.result.setAttribute('aria-atomic', 'true');
      card.appendChild(this.result);

      this.replaceChildren(card);
      this.renderMethod();
    }

    setMethod(index) {
      if (index === this.activeMethodIndex) return;
      this.activeMethodIndex = index;

      if (this.tabs) {
        Array.from(this.tabs.children).forEach((tab, tabIndex) => {
          const selected = tabIndex === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });
      }

      this.renderMethod();
      this.setResult('', '');
    }

    renderMethod() {
      const method = this.config.methods[this.activeMethodIndex];
      const fields = document.createElement('div');
      fields.className = 'size-recommender__fields';
      fields.dataset.sizeRecommenderMethod = method.id;

      method.inputs.forEach((inputConfig) => {
        const field = document.createElement('div');
        field.className = 'size-recommender__field';

        const label = document.createElement('label');
        label.className = 'size-recommender__label';
        label.htmlFor = `${this.instanceId}-${method.id}-${inputConfig.id}`;
        label.textContent = inputConfig.label;
        field.appendChild(label);

        const controlWrap = document.createElement('div');
        controlWrap.className = 'size-recommender__control-wrap';
        const control = inputConfig.type === 'select' ? document.createElement('select') : document.createElement('input');
        control.className = 'size-recommender__control';
        control.id = label.htmlFor;
        control.dataset.sizeRecommenderInput = inputConfig.id;
        control.required = true;

        if (inputConfig.type === 'select') {
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = inputConfig.placeholder || this.config.selectPlaceholder || '';
          placeholder.disabled = true;
          placeholder.selected = true;
          control.appendChild(placeholder);

          inputConfig.options.forEach((value) => {
            const option = document.createElement('option');
            option.value = String(value);
            option.textContent = String(value);
            control.appendChild(option);
          });

          const icon = document.createElement('span');
          icon.className = 'size-recommender__select-icon';
          icon.setAttribute('aria-hidden', 'true');
          controlWrap.appendChild(control);
          controlWrap.appendChild(icon);
        } else {
          control.type = 'number';
          control.inputMode = 'decimal';
          control.step = inputConfig.step || 'any';
          control.min = inputConfig.min;
          control.max = inputConfig.max;
          control.placeholder = inputConfig.placeholder || this.config.numberPlaceholder || '';
          controlWrap.appendChild(control);

          if (inputConfig.unit) {
            const unit = document.createElement('span');
            unit.className = 'size-recommender__unit';
            unit.setAttribute('aria-hidden', 'true');
            unit.textContent = inputConfig.unit;
            controlWrap.appendChild(unit);
          }
        }

        control.addEventListener('input', () => {
          control.removeAttribute('aria-invalid');
          this.setResult('', '');
        });
        control.addEventListener('change', () => {
          control.removeAttribute('aria-invalid');
          this.setResult('', '');
        });

        field.appendChild(controlWrap);
        fields.appendChild(field);
      });

      this.panel.replaceChildren(fields);
      if (this.config.methods.length > 1) {
        this.panel.setAttribute('aria-labelledby', `${this.instanceId}-tab-${method.id}`);
        this.panel.setAttribute('role', 'tabpanel');
      } else {
        this.panel.setAttribute('aria-labelledby', `${this.instanceId}-title`);
        this.panel.removeAttribute('role');
      }
    }

    calculate() {
      const method = this.config.methods[this.activeMethodIndex];
      const inputState = this.readInputs(method);

      if (!inputState.valid) {
        const message = inputState.outsideRange
          ? method.calculation.outsideRangeMessage
          : this.config.messages.completeFields;
        this.setResult(message || '', 'error');
        return;
      }

      const outcome = this.resolve(method, inputState.values);
      if (!outcome.size) {
        this.setResult(outcome.message || '', 'warning');
        return;
      }

      const successMessage = this.interpolate(this.config.messages.success, outcome.size);
      const selected = this.selectNativeOption(outcome.size);
      this.dataset.recommendedSize = outcome.size;

      if (selected) {
        this.setResult(successMessage, 'success');
        return;
      }

      const unavailable = this.interpolate(this.config.messages.nativeSizeUnavailable, outcome.size);
      this.setResult(`${successMessage} ${unavailable}`.trim(), 'warning');
    }

    readInputs(method) {
      const values = {};
      let valid = true;
      let outsideRange = false;

      method.inputs.forEach((inputConfig) => {
        const input = this.panel.querySelector(`[data-size-recommender-input="${inputConfig.id}"]`);
        input.removeAttribute('aria-invalid');

        if (inputConfig.type === 'number') {
          const value = Number.parseFloat(input.value);
          const belowMinimum = Number.isFinite(value) && value < Number(inputConfig.min);
          const aboveMaximum = Number.isFinite(value) && value > Number(inputConfig.max);

          if (!Number.isFinite(value) || value <= 0 || belowMinimum || aboveMaximum) {
            input.setAttribute('aria-invalid', 'true');
            valid = false;
            outsideRange = outsideRange || belowMinimum || aboveMaximum;
            return;
          }

          values[inputConfig.id] = value;
          return;
        }

        if (!input.value) {
          input.setAttribute('aria-invalid', 'true');
          valid = false;
          return;
        }

        values[inputConfig.id] = input.value;
      });

      return { values, valid, outsideRange };
    }

    resolve(method, values) {
      const calculation = method.calculation;

      if (calculation.type === 'exact_lookup') {
        const key = calculation.inputOrder.map((inputId) => values[inputId]).join(calculation.separator || '');
        const size = calculation.mapping[key];
        return size
          ? { size }
          : { size: null, message: calculation.noMatchMessage };
      }

      const resolvedSizes = method.inputs.map((input) => this.resolveRangeSize(
        input.id,
        values[input.id],
        calculation.ranges
      ));

      if (resolvedSizes.some((size) => !size)) {
        return { size: null, message: calculation.outsideRangeMessage };
      }

      if (calculation.type === 'same_size_ranges') {
        const sameSize = resolvedSizes.every((size) => size === resolvedSizes[0]);
        return sameSize
          ? { size: resolvedSizes[0] }
          : { size: null, message: calculation.mismatchMessage };
      }

      const indexes = resolvedSizes.map((size) => this.config.sizeOrder.indexOf(size));
      if (indexes.some((index) => index < 0)) return { size: null, message: calculation.outsideRangeMessage };
      return { size: this.config.sizeOrder[Math.max(...indexes)] };
    }

    resolveRangeSize(inputId, value, ranges) {
      return this.config.sizeOrder.find((size) => {
        const range = ranges[size] && ranges[size][inputId];
        if (!range) return false;
        const minimumMatches = range.minInclusive === false ? value > range.min : value >= range.min;
        const maximumMatches = range.maxInclusive === false ? value < range.max : value <= range.max;
        return minimumMatches && maximumMatches;
      }) || null;
    }

    getNativeOptionInputs() {
      return Array.from(this.scope.querySelectorAll('[name^="options["]')).filter(
        (input) => input.name === this.nativeOptionName
      );
    }

    selectNativeOption(size) {
      const inputs = this.getNativeOptionInputs();
      const radio = inputs.find((input) => input.type === 'radio' && input.value === size);

      if (radio) {
        if (radio.checked) return true;
        const label = Array.from(this.scope.querySelectorAll('label')).find((item) => item.htmlFor === radio.id);
        if (label) label.click();
        else radio.click();
        return radio.checked;
      }

      const popoutInput = inputs.find((input) => input.matches('[data-popout-input]'));
      if (popoutInput) {
        if (popoutInput.value === size) return true;
        const popout = popoutInput.closest('[data-popout]');
        const option = popout && Array.from(popout.querySelectorAll('[data-popout-option]')).find(
          (item) => item.dataset.value === size
        );
        if (option) option.click();
        return popoutInput.value === size;
      }

      const select = inputs.find((input) => input.tagName === 'SELECT');
      if (select) {
        const option = Array.from(select.options).find((item) => item.value === size);
        if (!option) return false;
        option.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return select.value === size;
      }

      return false;
    }

    interpolate(template, size) {
      return String(template || '').replace(/\{\{\s*size\s*\}\}/g, size);
    }

    setResult(message, state) {
      this.result.textContent = message;
      if (state) this.result.dataset.state = state;
      else this.result.removeAttribute('data-state');
    }
  }

  customElements.define('size-recommender', SizeRecommender);
})();
